export type AppIconName =
  | 'activity'
  | 'alert'
  | 'arrowRight'
  | 'audits'
  | 'chevronDown'
  | 'chevronRight'
  | 'chevronUp'
  | 'close'
  | 'cube'
  | 'dashboard'
  | 'document'
  | 'layers'
  | 'families'
  | 'inbox'
  | 'lock'
  | 'menu'
  | 'projects'
  | 'search'
  | 'snapshots'
  | 'sortNeutral'
  | 'sparkles'
  | 'user';

export interface AppIconProps {
  readonly name: AppIconName;
  readonly size?: number;
  readonly strokeWidth?: number;
}

/** Small, dependency-free icon set with a consistent rounded outline. */
export function AppIcon({ name, size = 20, strokeWidth = 1.8 }: AppIconProps): React.JSX.Element {
  const glyph = (() => {
    switch (name) {
      case 'dashboard':
        return (
          <>
            <rect x="3" y="3" width="7" height="7" rx="2" />
            <rect x="14" y="3" width="7" height="7" rx="2" />
            <rect x="3" y="14" width="7" height="7" rx="2" />
            <rect x="14" y="14" width="7" height="7" rx="2" />
          </>
        );
      case 'projects':
        return (
          <>
            <path d="M3.5 7.5h6l2-2h9v13a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
            <path d="M3.5 10.5h17" />
          </>
        );
      case 'snapshots':
        return (
          <>
            <path d="m12 3 9 4.5-9 4.5-9-4.5z" />
            <path d="m3 12 9 4.5 9-4.5" />
            <path d="m3 16.5 9 4.5 9-4.5" />
          </>
        );
      case 'audits':
        return (
          <>
            <path d="M9 5h6" />
            <path d="M9 3h6a2 2 0 0 1 2 2v1h2v15H5V6h2V5a2 2 0 0 1 2-2Z" />
            <path d="m8.5 13 2 2 5-5" />
          </>
        );
      case 'families':
        return (
          <>
            <rect x="3" y="4" width="8" height="7" rx="2" />
            <rect x="13" y="4" width="8" height="7" rx="2" />
            <rect x="3" y="13" width="8" height="7" rx="2" />
            <rect x="13" y="13" width="8" height="7" rx="2" />
          </>
        );
      case 'user':
        return (
          <>
            <circle cx="12" cy="8" r="4" />
            <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
          </>
        );
      case 'search':
        return (
          <>
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="m16 16 4.5 4.5" />
          </>
        );
      case 'activity':
        return <path d="M3 12h4l2.5-7 5 14 2.5-7h4" />;
      case 'document':
        return (
          <>
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <path d="M14 3v5h5" />
            <path d="M9 13h6M9 17h6" />
          </>
        );
      case 'layers':
        return (
          <>
            <rect x="4" y="4" width="16" height="16" rx="2" />
            <path d="M4 10h16M10 10v10" />
          </>
        );
      case 'sparkles':
        return (
          <>
            <path d="m12 3 1.4 3.6L17 8l-3.6 1.4L12 13l-1.4-3.6L7 8l3.6-1.4z" />
            <path d="m18.5 14 .8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
            <path d="m5.5 14 .7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
          </>
        );
      case 'inbox':
        return (
          <>
            <path d="M4 5h16l1 10v5H3v-5z" />
            <path d="M3 15h5l1.5 2h5l1.5-2h5" />
          </>
        );
      case 'alert':
        return (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v6" />
            <path d="M12 17h.01" />
          </>
        );
      case 'chevronDown':
        return <path d="m7 9.5 5 5 5-5" />;
      case 'chevronUp':
        return <path d="m7 14.5 5-5 5 5" />;
      case 'chevronRight':
        return <path d="m9.5 7 5 5-5 5" />;
      case 'menu':
        return (
          <>
            <path d="M4 7h16" />
            <path d="M4 12h16" />
            <path d="M4 17h16" />
          </>
        );
      case 'close':
        return (
          <>
            <path d="m6 6 12 12" />
            <path d="m18 6-12 12" />
          </>
        );
      case 'arrowRight':
        return <path d="M4 12h14m0 0-5-5m5 5-5 5" />;
      case 'cube':
        return (
          <>
            <path d="M12 3 4 7.5v9L12 21l8-4.5v-9Z" />
            <path d="M4 7.5 12 12l8-4.5" />
            <path d="M12 12v9" />
          </>
        );
      case 'lock':
        return (
          <>
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </>
        );
      case 'sortNeutral':
        return (
          <>
            <path d="m7 10 5-5 5 5" />
            <path d="m7 14 5 5 5-5" />
          </>
        );
    }
  })();

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {glyph}
    </svg>
  );
}
